import React, { useState } from "react";
import { Dialog } from "primereact/dialog";
import { TabView, TabPanel } from "primereact/tabview";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import "./UserSettings.css";

const WORLD_CURRENCIES = [
  { label: "US Dollar (USD)", value: "USD" },
  { label: "Euro (EUR)", value: "EUR" },
  { label: "British Pound (GBP)", value: "GBP" },
  { label: "Japanese Yen (JPY)", value: "JPY" },
  { label: "Swiss Franc (CHF)", value: "CHF" },
  { label: "Canadian Dollar (CAD)", value: "CAD" },
  { label: "Australian Dollar (AUD)", value: "AUD" },
  { label: "Chinese Yuan (CNY)", value: "CNY" },
  { label: "Swedish Krona (SEK)", value: "SEK" },
  { label: "Norwegian Krone (NOK)", value: "NOK" },
  { label: "Danish Krone (DKK)", value: "DKK" },
  { label: "Singapore Dollar (SGD)", value: "SGD" },
  { label: "Hong Kong Dollar (HKD)", value: "HKD" },
  { label: "South Korean Won (KRW)", value: "KRW" },
  { label: "Indian Rupee (INR)", value: "INR" },
  { label: "Brazilian Real (BRL)", value: "BRL" },
  { label: "Mexican Peso (MXN)", value: "MXN" },
  { label: "South African Rand (ZAR)", value: "ZAR" },
  { label: "Russian Ruble (RUB)", value: "RUB" },
  { label: "Turkish Lira (TRY)", value: "TRY" },
  { label: "Polish Zloty (PLN)", value: "PLN" },
  { label: "Thai Baht (THB)", value: "THB" },
  { label: "New Zealand Dollar (NZD)", value: "NZD" },
  { label: "Israeli Shekel (ILS)", value: "ILS" },
  { label: "Czech Koruna (CZK)", value: "CZK" },
].sort((a, b) => a.label.localeCompare(b.label));

const AI_MODELS = [
  { label: "GPT-4o Mini (Fast & Affordable)", value: "gpt-4o-mini" },
  { label: "GPT-4o (Highest Quality)", value: "gpt-4o" },
  {
    label: "Claude 3.5 Haiku (Fast & Cheap)",
    value: "claude-3-5-haiku-20241022",
  },
  {
    label: "Claude 3.5 Sonnet (Best Reasoning)",
    value: "claude-3-5-sonnet-20241022",
  },
];

interface UserSettingsProps {
  visible: boolean;
  onHide: () => void;
  headerElement: React.ReactNode;
  footerContent: React.ReactNode;
  currency: string;
  setCurrency: (value: string) => void;
  showWhyTag: boolean;
  setShowWhyTag: (value: boolean) => void;
  showStartupDialog: boolean;
  setShowStartupDialog: (value: boolean) => void;
  showAIPromptOnNewMap: boolean;
  setShowAIPromptOnNewMap: (value: boolean) => void;
  showGettingStarted: boolean;
  setShowGettingStarted: (value: boolean) => void;
  aiModel: string;
  setAiModel: (value: string) => void;
}

const UserSettings: React.FC<UserSettingsProps> = ({
  visible,
  onHide,
  headerElement,
  footerContent,
  currency,
  setCurrency,
  showWhyTag,
  setShowWhyTag,
  showStartupDialog,
  setShowStartupDialog,
  showAIPromptOnNewMap,
  setShowAIPromptOnNewMap,
  showGettingStarted,
  setShowGettingStarted,
  aiModel,
  setAiModel,
}) => {
  return (
    <Dialog
      visible={visible}
      modal
      header={headerElement}
      footer={footerContent}
      style={{ width: "600px", height: "auto" }}
      onHide={onHide}
    >
      <TabView className="user-settings-p-tabview-nav">
        <TabPanel header="General">
          <div style={{ padding: "20px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <label
                htmlFor="currency"
                style={{
                  minWidth: "100px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Currency:
              </label>
              <Dropdown
                className="currency-dropdown"
                id="currency"
                value={currency}
                options={WORLD_CURRENCIES}
                onChange={(e) => setCurrency(e.value)}
                placeholder="Select a currency"
                filter
                style={{ width: "100%", fontSize: "14px", height: "43px" }}
              />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <div className="green-switch-wrapper">
                  <InputSwitch
                    inputId="showWhyTag"
                    checked={showWhyTag}
                    onChange={(e) => setShowWhyTag(e.value)}
                  />
                </div>
                <span
                  style={{ fontSize: "14px", fontWeight: "500", color: "#666" }}
                >
                  Display the "Why?" tag on the Goal node
                </span>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <div className="green-switch-wrapper">
                  <InputSwitch
                    inputId="showStartupDialog"
                    checked={showStartupDialog}
                    onChange={(e) => setShowStartupDialog(e.value)}
                  />
                </div>
                <span
                  style={{ fontSize: "14px", fontWeight: "500", color: "#666" }}
                >
                  Show 'Guided Creation Flow' when starting a new map
                </span>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <div className="green-switch-wrapper">
                  <InputSwitch
                    inputId="showAIPromptOnNewMap"
                    checked={showAIPromptOnNewMap}
                    onChange={(e) => setShowAIPromptOnNewMap(e.value)}
                  />
                </div>
                <span
                  style={{ fontSize: "14px", fontWeight: "500", color: "#666" }}
                >
                  Show AI prompt window when creating a new map
                </span>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <div className="green-switch-wrapper">
                  <InputSwitch
                    inputId="showGettingStarted"
                    checked={showGettingStarted}
                    onChange={(e) => setShowGettingStarted(e.value)}
                  />
                </div>
                <span
                  style={{ fontSize: "14px", fontWeight: "500", color: "#666" }}
                >
                  Show 'Getting Started' video on new map creation
                </span>
              </div>
            </div>
          </div>
        </TabPanel>
        <TabPanel header="AI">
          <div style={{ padding: "20px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <label
                htmlFor="aiModel"
                style={{
                  minWidth: "100px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                AI Model:
              </label>
              <Dropdown
                id="aiModel"
                value={aiModel}
                options={AI_MODELS}
                onChange={(e) => setAiModel(e.value)}
                placeholder="Select AI model"
                style={{ width: "100%", fontSize: "14px", height: "43px" }}
              />
            </div>
            <div style={{ fontSize: "12px", color: "#666", marginTop: "10px" }}>
              <p style={{ marginBottom: "8px" }}>
                <strong>Model Comparison:</strong>
              </p>
              <ul style={{ paddingLeft: "20px", margin: 0 }}>
                <li style={{ marginBottom: "5px" }}>
                  <strong>GPT-4o Mini:</strong> Best balance of speed and cost.
                  Recommended for most tasks.
                </li>
                <li style={{ marginBottom: "5px" }}>
                  <strong>GPT-4o:</strong> Highest quality for complex
                  structures with many relationships.
                </li>
                <li style={{ marginBottom: "5px" }}>
                  <strong>Claude 3.5 Haiku:</strong> Fastest and most affordable
                  option.
                </li>
                <li style={{ marginBottom: "5px" }}>
                  <strong>Claude 3.5 Sonnet:</strong> Best for complex reasoning
                  and understanding relationships.
                </li>
              </ul>
            </div>
          </div>
        </TabPanel>
      </TabView>
    </Dialog>
  );
};

export default UserSettings;
